import React, {
  Children,
  cloneElement,
  createElement,
  isValidElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { get } from 'lodash';
import { DatagridLoading } from 'react-admin';
import classnames from 'classnames';

import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Checkbox from '@material-ui/core/Checkbox';
import LinearProgress from '@material-ui/core/LinearProgress';
import { RadioButtonUnchecked } from '@material-ui/icons';

import { useColumnConfig } from 'components/layout/ColumnsConfigButton';
import DatagridHeaderCell from 'components/list/DatagridHeaderCell';
import DatagridBody from './DatagridBody';

import { ReactComponent as CheckIcon } from 'assets/general/icons/CheckIcon.svg';

const DELAYED_LOADING_TIMEOUT = 0;
const DATAGRID_HEADER_HEIGHT = 52;
const DATAGRID_PROGRESS_HEIGHT = 4;
const BULK_ACTIONS_SELECTOR = '[data-datagrid-bulk-actions="true"]';

const areMeasurementsEqual = (current: number[], next: number[]) =>
  current.length === next.length &&
  current.every((width, index) => Math.abs(width - next[index]) <= 1);

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    contain: 'inline-size',
  },
  table: {
    tableLayout: 'auto',
    minWidth: '100%',
  },
  stickyHeader: {
    position: 'sticky',
    top: 0,
    zIndex: theme.zIndex.appBar,
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    height: DATAGRID_HEADER_HEIGHT,
    marginBottom: -DATAGRID_HEADER_HEIGHT,
    boxSizing: 'border-box',
    pointerEvents: 'none',
  },
  stickyHeaderLoading: {
    height: DATAGRID_HEADER_HEIGHT + DATAGRID_PROGRESS_HEIGHT,
  },
  stickyHeaderScroll: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    overflowX: 'auto',
    overflowY: 'hidden',
    pointerEvents: 'auto',
    boxShadow: `inset 0 -1px 0 ${theme.palette.divider}`,
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  stickyHeaderTable: {
    tableLayout: 'fixed',
    minWidth: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
  },
  stickyHeaderProgress: {
    minWidth: '100%',
    backgroundColor: theme.palette.secondary.main,
  },
  tableWrapper: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    overflowX: 'auto',
    overflowY: 'visible',
  },
  thead: {},
  tbody: {
    height: 'inherit',
  },
  headerRow: {
    textAlign: 'left',
    '& > *:first-child': {
      borderTopLeftRadius: theme.spacing(2),
    },
    '& > *:last-child': {
      borderTopRightRadius: theme.spacing(2),
    },
  },
  headerCell: {},
  stickyHeaderCell: {
    backgroundColor: theme.palette.secondary.main,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  headerSpacerCell: {
    visibility: 'hidden',
    pointerEvents: 'none',
    borderBottom: 0,
  },
  checkbox: {},
  row: {},
  clickableRow: {
    cursor: 'pointer',
  },
  rowEven: {},
  rowOdd: {},
  rowCell: {},
  expandRoot: {},
  expandHeader: {
    padding: 0,
    width: theme.spacing(6),
  },
  expandCell: {},
  expandIconCell: {
    width: theme.spacing(6),
    paddingLeft: theme.spacing(2),
  },
  expandIcon: {
    padding: theme.spacing(1),
    transform: 'rotate(-90deg)',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expanded: {
    transform: 'rotate(0deg)',
  },
  dataLoading: {
    '&  div': {
      borderRadius: '50px',
    },
  },
}));

const Datagrid = ({
  classes: classesOverride,
  ...props
}: {
  classes?: any;
  [key: string]: any;
}) => {
  const classes = useStyles({ classes: classesOverride });
  const datagridRootRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);
  const tableWrapperRef = useRef<HTMLDivElement | null>(null);
  const stickyHeaderScrollRef = useRef<HTMLDivElement | null>(null);
  const lastHeaderMeasurementRef = useRef<{
    cellWidths: number[];
    tableWidth: number;
  } | null>(null);

  const {
    basePath,
    body = <DatagridBody />,
    children,
    className,
    currentSort,
    data = {},
    expand,
    hasBulkActions = false,
    hover,
    ids = [],
    loading,
    loaded,
    onSelect,
    onToggleItem,
    resource,
    rowClick,
    rowStyle,
    showHeader = true,
    selectedIds = [],
    setSort,
    total,
    version,
    widths,
    nbFakeLines,
    ...rest
  } = props;

  const [showDelayedLoading, setShowDelayedLoading] = useState<boolean>(false);
  const [headerCellWidths, setHeaderCellWidths] = useState<number[]>([]);
  const [headerTableWidth, setHeaderTableWidth] = useState<number | undefined>();
  const [stickyHeaderOffset, setStickyHeaderOffset] = useState<number>(0);

  useEffect(() => {
    if (!loading || loaded === false) {
      setShowDelayedLoading(false);
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      setShowDelayedLoading(true);
    }, DELAYED_LOADING_TIMEOUT);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [loaded, loading]);

  const updateSort = useCallback(
    (event) => {
      event.stopPropagation();
      const { sort, direction } = event.currentTarget.dataset;
      setSort(
        sort,
        currentSort.field === sort ? (direction === 'ASC' ? 'DESC' : 'ASC') : direction
      );
    },
    [setSort] //eslint-disable-line
  );

  const handleSelectAll = useCallback(
    (event) => {
      if (event.target.checked) {
        onSelect(ids.concat(selectedIds.filter((id) => !ids.includes(id))));
      } else {
        onSelect([]);
      }
    },
    [ids, onSelect, selectedIds]
  );

  const { data: colConfig, loading: colConfLoading } = useColumnConfig(resource);
  const visibleChildren = Children.toArray(children).filter((field: any) =>
    get(colConfig, `fields.${field.props.source}`, true)
  );
  const nbColumns = visibleChildren.length + (expand ? 1 : 0) + (hasBulkActions ? 1 : 0);

  useLayoutEffect(() => {
    if (!showHeader || loaded === false || colConfLoading) {
      return undefined;
    }

    const table = tableRef.current;
    const tableWrapper = tableWrapperRef.current;
    if (!table || !tableWrapper) {
      return undefined;
    }

    let frame = 0;

    const updateHeaderMeasurements = () => {
      const headerCells = table.querySelectorAll(
        'thead tr:first-child > th, thead tr:first-child > td'
      );
      const nextCellWidths = Array.from(headerCells).map((cell) =>
        Math.round((cell as HTMLElement).getBoundingClientRect().width)
      );
      const nextTableWidth = Math.ceil(
        Math.max(
          table.scrollWidth,
          table.getBoundingClientRect().width,
          tableWrapper.getBoundingClientRect().width
        )
      );
      const previousMeasurement = lastHeaderMeasurementRef.current;
      const measurementsChanged =
        !previousMeasurement ||
        !areMeasurementsEqual(previousMeasurement.cellWidths, nextCellWidths) ||
        Math.abs(previousMeasurement.tableWidth - nextTableWidth) > 1;

      if (measurementsChanged) {
        lastHeaderMeasurementRef.current = {
          cellWidths: nextCellWidths,
          tableWidth: nextTableWidth,
        };
        setHeaderCellWidths(nextCellWidths);
        setHeaderTableWidth(nextTableWidth);
      }

      if (stickyHeaderScrollRef.current) {
        stickyHeaderScrollRef.current.scrollLeft = tableWrapper.scrollLeft;
      }
    };

    const scheduleHeaderMeasurements = () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }

      frame = window.requestAnimationFrame(() => {
        frame = 0;
        updateHeaderMeasurements();
      });
    };

    updateHeaderMeasurements();
    scheduleHeaderMeasurements();

    window.addEventListener('resize', scheduleHeaderMeasurements);

    const ResizeObserverConstructor = (window as any).ResizeObserver;
    const resizeObserver = ResizeObserverConstructor
      ? new ResizeObserverConstructor(scheduleHeaderMeasurements)
      : null;

    if (resizeObserver) {
      resizeObserver.observe(table);
      resizeObserver.observe(tableWrapper);
    }

    return () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }

      window.removeEventListener('resize', scheduleHeaderMeasurements);

      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [
    colConfLoading,
    expand,
    hasBulkActions,
    ids.length,
    loaded,
    nbColumns,
    showHeader,
    total,
    widths,
  ]);

  useLayoutEffect(() => {
    if (!showHeader || !hasBulkActions || loaded === false || colConfLoading) {
      setStickyHeaderOffset(0);
      return undefined;
    }

    const datagridRoot = datagridRootRef.current;
    const bulkActions = datagridRoot?.parentElement?.querySelector(
      BULK_ACTIONS_SELECTOR
    ) as HTMLElement | null;

    if (!bulkActions) {
      setStickyHeaderOffset(0);
      return undefined;
    }

    let frame = 0;

    const updateBulkActionsOffset = () => {
      const nextOffset = Math.round(bulkActions.getBoundingClientRect().height);

      setStickyHeaderOffset((current) =>
        Math.abs(current - nextOffset) <= 1 ? current : nextOffset
      );
    };

    const scheduleBulkActionsOffset = () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }

      frame = window.requestAnimationFrame(updateBulkActionsOffset);
    };

    updateBulkActionsOffset();
    scheduleBulkActionsOffset();

    window.addEventListener('resize', scheduleBulkActionsOffset);
    bulkActions.addEventListener('transitionend', scheduleBulkActionsOffset);

    const ResizeObserverConstructor = (window as any).ResizeObserver;
    const resizeObserver = ResizeObserverConstructor
      ? new ResizeObserverConstructor(scheduleBulkActionsOffset)
      : null;

    if (resizeObserver) {
      resizeObserver.observe(bulkActions);
    }

    return () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }

      window.removeEventListener('resize', scheduleBulkActionsOffset);
      bulkActions.removeEventListener('transitionend', scheduleBulkActionsOffset);

      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [colConfLoading, hasBulkActions, loaded, selectedIds.length, showHeader]);

  const syncStickyHeaderScroll = useCallback((event) => {
    const stickyHeaderScroll = stickyHeaderScrollRef.current;

    if (stickyHeaderScroll && stickyHeaderScroll.scrollLeft !== event.currentTarget.scrollLeft) {
      stickyHeaderScroll.scrollLeft = event.currentTarget.scrollLeft;
    }
  }, []);

  const syncTableScroll = useCallback((event) => {
    const tableWrapper = tableWrapperRef.current;

    if (tableWrapper && tableWrapper.scrollLeft !== event.currentTarget.scrollLeft) {
      tableWrapper.scrollLeft = event.currentTarget.scrollLeft;
    }
  }, []);

  const renderColGroup = (columnWidths = widths) =>
    columnWidths ? (
      <colgroup>
        {columnWidths.map((width, index) => (
          <col key={index} width={typeof width === 'number' ? `${width}px` : width} />
        ))}
      </colgroup>
    ) : null;

  const renderHeader = (cellClassName, isSpacer = false) =>
    showHeader ? (
      <TableHead className={classes.thead}>
        <TableRow className={classnames(classes.row, classes.headerRow)}>
          {expand && (
            <TableCell padding="none" className={classnames(classes.expandHeader, cellClassName)} />
          )}
          {hasBulkActions && (
            <TableCell padding="none" className={cellClassName}>
              <Checkbox
                className="select-all"
                icon={<RadioButtonUnchecked />}
                checkedIcon={<CheckIcon />}
                checked={
                  selectedIds.length > 0 &&
                  ids.length > 0 &&
                  ids.every((id) => selectedIds.includes(id))
                }
                onChange={handleSelectAll}
                disableTouchRipple
                disableRipple
              />
            </TableCell>
          )}
          {Children.map(children, (field, index) => {
            const { props } = field;
            const showColumn = get(colConfig, `fields.${props.source}`, true);

            if (props.header) {
              return (
                <TableCell
                  key={props.source || index}
                  className={classnames(classes.headerCell, cellClassName)}
                  align={props.textAlign}
                  variant="head">
                  {isSpacer
                    ? null
                    : createElement(props.header, {
                        fields: children,
                        basePath,
                        resource,
                      })}
                </TableCell>
              );
            }

            return isValidElement(field) && showColumn ? (
              <DatagridHeaderCell
                className={classnames(classes.headerCell, cellClassName)}
                currentSort={currentSort}
                field={field}
                isSorting={currentSort.field === (props.sortBy || props.source)}
                key={props.source || index}
                resource={resource}
                updateSort={updateSort}
                padding="none"
              />
            ) : null;
          })}
        </TableRow>
      </TableHead>
    ) : null;

  if (loaded === false || colConfLoading) {
    return (
      <DatagridLoading
        classes={classes}
        className={classnames(className, classes.dataLoading)}
        expand={expand}
        hasBulkActions={hasBulkActions}
        nbChildren={React.Children.count(children)}
        nbFakeLines={nbFakeLines}
      />
    );
  }

  if (loaded && (ids.length === 0 || total === 0) && !showDelayedLoading) {
    return null;
  }

  const measuredHeaderWidths = headerCellWidths.length === nbColumns ? headerCellWidths : widths;
  const stickyHeaderTableStyle = headerTableWidth ? { width: headerTableWidth } : undefined;
  const stickyHeaderStyle = stickyHeaderOffset ? { top: stickyHeaderOffset } : undefined;

  return (
    <div className={classes.root} ref={datagridRootRef}>
      {showHeader && (
        <div
          className={classnames(
            classes.stickyHeader,
            showDelayedLoading && classes.stickyHeaderLoading
          )}
          style={stickyHeaderStyle}>
          <div
            className={classes.stickyHeaderScroll}
            ref={stickyHeaderScrollRef}
            onScroll={syncTableScroll}>
            <Table className={classes.stickyHeaderTable} style={stickyHeaderTableStyle}>
              {renderColGroup(measuredHeaderWidths)}
              {renderHeader(classes.stickyHeaderCell)}
            </Table>
            {showDelayedLoading && (
              <div className={classes.stickyHeaderProgress} style={stickyHeaderTableStyle}>
                <LinearProgress />
              </div>
            )}
          </div>
        </div>
      )}
      <div className={classes.tableWrapper} ref={tableWrapperRef} onScroll={syncStickyHeaderScroll}>
        <Table className={classnames(classes.table, className)} data-test="datagrid" ref={tableRef}>
          {renderColGroup(widths)}
          {renderHeader(classes.headerSpacerCell, true)}
          {cloneElement(
            body,
            {
              basePath,
              className: classes.tbody,
              classes,
              expand,
              rowClick,
              data,
              hasBulkActions,
              hover,
              ids,
              onToggleItem,
              resource,
              rowStyle,
              selectedIds,
              version,
              ...rest,
            },
            visibleChildren
          )}
        </Table>
      </div>
    </div>
  );
};

export default Datagrid;
